import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { EventBus } from '@servicetitan/web-components';
import appManager from '@app/appManager';

const logger = appManager.logger('Inbox');

/**
 * Calls the latest `handler` for each `event` customer-mfe sends on the
 * loader's bus, for the adapter's mounted life. The bus mounts with the
 * loader, so pass what mounts the loader as `mountKey`. A handler that throws
 * is logged, not thrown back into customer-mfe, which emits synchronously.
 */
export default function useBusEvent<P>(
  busRef: RefObject<EventBus | null>,
  event: string,
  handler: ((payload: P) => void) | undefined,
  mountKey: unknown,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const bus = busRef.current;
    if (!bus) {
      return;
    }
    // the bus is untyped; customer-mfe sends a P with this event
    const listener = (payload: unknown) => {
      try {
        handlerRef.current?.(payload as P);
      } catch (error) {
        logger.error(`customer-mfe ${event} handler failed`, error);
      }
    };
    bus.on(event, listener);
    return () => {
      bus.off(event, listener);
    };
  }, [busRef, event, mountKey]);
}
